/**
 * The Singleton class defines the `getInstance` method that lets clients access
 * the unique singleton instance.
 */
export class ExcelManager {
  private static instance: ExcelManager
  private static XLSX = require('xlsx')

  /**
   * The Singleton's constructor should always be private to prevent direct
   * construction calls with the `new` operator.
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  /**
   * The static method that controls the access to the singleton instance.
   *
   * This implementation let you subclass the Singleton class while keeping
   * just one instance of each subclass around.
   */
  public static getInstance(): ExcelManager {
    if (!ExcelManager.instance) {
      ExcelManager.instance = new ExcelManager()
    }
    return ExcelManager.instance
  }

  /**
   * Finally, any singleton should define some business logic, which can be
   * executed on its instance.
   */
  public downloadExcel = (filename: string, data: (string | number)[][], wsName: string): void => {
    const wb = ExcelManager.XLSX.utils.book_new(),
      ws = ExcelManager.XLSX.utils.aoa_to_sheet(data)
    this.setColumnWidthForWorksheet(data, ws)

    /* add worksheet to workbook */
    ExcelManager.XLSX.utils.book_append_sheet(wb, ws, wsName)

    /* write workbook */
    ExcelManager.XLSX.writeFile(wb, filename)
  }

  public downloadExcelWithMultipleSheets = (
    filename: string,
    sheets: { name: string; data: (string | number)[][] }[],
  ): void => {
    const wb = ExcelManager.XLSX.utils.book_new()

    sheets.forEach(({ name, data }) => {
      const ws = ExcelManager.XLSX.utils.aoa_to_sheet(data)
      this.setColumnWidthForWorksheet(data, ws)
      ExcelManager.XLSX.utils.book_append_sheet(wb, ws, name.substring(0, 31))
    })

    ExcelManager.XLSX.writeFile(wb, filename)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private setColumnWidthForWorksheet = (data: (string | number)[][], worksheet: any): void => {
    const objectMaxLength: number[] = []
    for (let i = 0; i < data.length; i++) {
      objectMaxLength[i] = 0
    }
    for (let i = 0; i < data.length; i++) {
      const element = data[i]
      for (let j = 0; j < element.length; j++) {
        if (typeof element[j] == 'number' && objectMaxLength[j] < 10) {
          objectMaxLength[j] = 10
        } else {
          objectMaxLength[j] =
            !element[j] || objectMaxLength[j] >= String(element[j]).length ? objectMaxLength[j] : String(element[j]).length
        }
      }
    }

    const wscols: {
      width: number
    }[] = []

    for (let i = 0; i < data[0].length; i++) {
      wscols.push({ width: objectMaxLength[i] + 2 })
    }

    worksheet['!cols'] = wscols
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async importExcelFile(uploadedFile: Blob): Promise<string[]> {
    const reader = new FileReader()
    return new Promise((resolve, reject) => {
      reader.onload = (e) => {
        const res: string[] = []
        let data
        if (e.target) {
          data = e.target.result
        } else {
          reject()
        }
        const workbook = ExcelManager.XLSX.read(data, { type: 'binary' })
        workbook.SheetNames.forEach((sheetName) => {
          const XL_row_object = ExcelManager.XLSX.utils.sheet_to_row_object_array(workbook.Sheets[sheetName])
          res.push(JSON.stringify(XL_row_object))
        })
        resolve(res)
      }

      reader.onerror = function (ex) {
        reject(ex)
      }

      reader.readAsArrayBuffer(uploadedFile)
    })
  }

  public async importExcelFileWithSheets(
    uploadedFile: Blob,
  ): Promise<{ sheetName: string; rows: Record<string, unknown>[] }[]> {
    const reader = new FileReader()
    return new Promise((resolve, reject) => {
      reader.onload = (e) => {
        if (!e.target?.result) {
          reject(new Error('Failed to read file'))
          return
        }

        const workbook = ExcelManager.XLSX.read(e.target.result, { type: 'array' })
        resolve(
          workbook.SheetNames.map((sheetName: string) => ({
            sheetName,
            rows: ExcelManager.XLSX.utils.sheet_to_json(workbook.Sheets[sheetName]) as Record<string, unknown>[],
          })),
        )
      }

      reader.onerror = (ex) => reject(ex)
      reader.readAsArrayBuffer(uploadedFile)
    })
  }
}
